import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { toast } from "react-toastify";
import axios from "axios";
import { Divider } from "ui-kit/Divider";
import { SmartForm } from "ui-kit/SmartForm";
import { Button } from "ui-kit/Button";
import IntlStore from "stories/IntlStore";
import { Routes } from "constants/routes";
import { API_BASE_URL, API_URLS } from "constants/api";
import { Step } from "./constants";
import { Layout } from "../Layout";
import { Title } from "../components/Title";
import { CountrySelect } from "../components/CountrySelect";
import { PhoneNumber } from "../components/PhoneNumber";
import { Input } from "../components/Input";
import { SocialNetworks } from "../components/SocialNetworks";
import { LoginPassword } from "../components/LoginPassword";
import { LoginSignUpToggle } from "../components/LoginSignUpToggle";
import { Verify } from "../SignUp/Verify";
import {
  compose,
  hasRequiredLength,
  hasUpperCase,
  hasValidEmail,
  isShowInputError,
  structuredValidator
} from "utils/validation";

interface FormValues {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  country: number;
  phone: string;
}

const MIN_PASSWORD_LENGTH = 8;
const MIN_NAME_LENGTH = 2;
const MIN_PHONE_LENGTH = 10;

export const SignUp: React.FC = () => {
  const [step, setStep] = useState(Step.Init);
  const [countries, setCountries] = useState([]);
  const [email, setEmail] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}${API_URLS.Countries}`);
        const {data} = response.data;

        if (data) {
          setCountries(data);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const onSubmit = async (values: FormValues) => {
    if (step === Step.Init) {
      setStep(Step.Form);
    } else {
      try {
        const response = await axios.post(`${API_BASE_URL}${API_URLS.SignUp}`, {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          password: values.password,
          country_id: values.country,
          phone_number: values.phone,
        });

        // @ts-expect-error: Need for analytic
        window.dataLayer = window.dataLayer||[];
        // @ts-expect-error: Need for analytic
        dataLayer.push({
          'event': 'custom_event',
          'event_category': 'form',
          'event_action': 'confirmed',
          'event_label': 'registration'
        });

        if (response?.data?.success) {
          setEmail(values.email);
          setStep(Step.Verify);
        }
      } catch (e: any) {
        const message = e.response.data.message;
        const errors = e.response.data.errors;
        console.error("Error", message);
        toast.error(message);

        if (errors) {
          Object.values(errors)
            .forEach((error: any) => {
              error.forEach((err: any) => toast.error(err.message));
            });
        }
      }
    }
  };

  const onResend = async () => {
    try {
      await axios.post(`${API_BASE_URL}${API_URLS.ResendSignUpVerification}`, { email });
    } catch (e: any) {
      const message = e.response.data.message;
      console.error("Error", message);
      toast.error(message);
      throw e;
    }
  };

  const onBack = () => setStep(Step.Form);

  if (step === Step.Verify) {
    return (
      <Layout>
        <Container>
          <Verify onBack={onBack} onResend={onResend} />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <SmartForm<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          email: '',
          password: '',
          firstname: '',
          lastname: '',
          country: 1,
          phone: '+1'
        }}
        validate={structuredValidator({
          email: hasValidEmail,
          password: step === Step.Form && compose(hasUpperCase, hasRequiredLength(MIN_PASSWORD_LENGTH)),
          firstname: step === Step.Form && hasRequiredLength(MIN_NAME_LENGTH),
          lastname: step === Step.Form && hasRequiredLength(MIN_NAME_LENGTH),
          phone: step === Step.Form && hasRequiredLength(MIN_PHONE_LENGTH),
        })}
      >
        {(props) => {
          const { handleSubmit, errors, submitFailed, submitting } = props;
          return (
            <Container id="sign-up" onSubmit={handleSubmit}>
              <Title>
                <FormattedMessage id="Auth.SignUp.Title" defaultMessage="Sign up for free"/>
              </Title>
              <SocialNetworks/>
              
              <LoginPassword
                step={step}
                errors={{
                  email: isShowInputError<FormValues>(props, 'email') && <FormattedMessage id={errors?.email} />,
                  password: isShowInputError<FormValues>(props, 'password') && <FormattedMessage id={errors?.password} values={{characters: MIN_PASSWORD_LENGTH}} />,
                }}
              />
              {step === Step.Form && (
                <>
                  <Divider $margin={3} $color="#ffffff" $opacity={0.15} />
                  <NameContainer>
                    <Input
                      name="firstname"
                      placeholder={IntlStore.intl.formatMessage({
                        id: "Auth.FirstNamePlaceholder",
                        defaultMessage: "First Name"
                      })}
                      info={IntlStore.intl.formatMessage({
                        id: "Auth.FirstNameInfo",
                        defaultMessage: "This name will be used for royalties"
                      })}
                      error={isShowInputError<FormValues>(props, 'firstname') && <FormattedMessage id={errors?.firstname} values={{characters: MIN_NAME_LENGTH}} />}
                    />
                    <Input
                      name="lastname"
                      placeholder={IntlStore.intl.formatMessage({
                        id: "Auth.LastNamePlaceholder",
                        defaultMessage: "Last Name"
                      })}
                      info={IntlStore.intl.formatMessage({
                        id: "Auth.LastNameInfo",
                        defaultMessage: "This name will be used for royalties"
                      })}
                      error={isShowInputError<FormValues>(props, 'lastname') && <FormattedMessage id={errors?.lastname} values={{characters: MIN_NAME_LENGTH}} />}
                    />
                  </NameContainer>
                  <AdditionalContainer>
                    <CountrySelect placeholder={'USA'} countries={countries} error={submitFailed ? errors?.country : null} />
                    <PhoneNumber  placeholder={'+1 555 123-4567'} value="" error={isShowInputError<FormValues>(props, 'phone') && <FormattedMessage id={errors?.phone} values={{characters: MIN_PHONE_LENGTH}} />} />
                  
                  </AdditionalContainer>
                </>
              )}
              <LastBlock>
                <Button type="submit" form="sign-up" loading={submitting}>
                  <FormattedMessage id="Auth.SignUpButton" defaultMessage="Sign up"/>
                </Button>
                <PrivacyPolicyText>
                  <FormattedMessage id="Auth.SignUpPrivacyPolicyPart1" defaultMessage="By creating an account you agree to the "/>
                  <PrivacyPolicyHighlightText>
                    <FormattedMessage id="Auth.SignUpTermsOfUse" defaultMessage="Terms of Use"/>
                  </PrivacyPolicyHighlightText>
                  <FormattedMessage id="Auth.SignUpPrivacyPolicyPart2" defaultMessage=" and "/>
                  <PrivacyPolicyHighlightText>
                    <FormattedMessage id="Auth.SignUpPrivacyPolicy" defaultMessage="Privacy Policy"/>
                  </PrivacyPolicyHighlightText>
                </PrivacyPolicyText>
                <LoginSignUpToggle
                  link={Routes.SignIn}
                  message={(
                    <FormattedMessage id="Auth.SignUpLogInMessage" defaultMessage="Already have an account?"/>
                  )}
                  linkText={(
                    <FormattedMessage id="Auth.SignUpLogInLink" defaultMessage="Log in"/>
                  )}
                />
              </LastBlock>
            </Container>
          );
        }}
      </SmartForm>
    </Layout>
  );
};

const Container = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 17px;
  padding: 54px;
  height: 100%;
  max-width: 548px;

  @media (max-width: 1200px) {
    max-width: 100%;
  }
`;

const NameContainer = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const AdditionalContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 15px;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const PrivacyPolicyText = styled.div`
  font-size: 13px;
  line-height: 19px;
  color: #7E7E7E;
`;

const PrivacyPolicyHighlightText = styled.a`
  color: #6777FF;
  cursor: pointer;
`;

const LastBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;
